import {MDIntent} from '@mintdata-wsp/common/components/MDIntent';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {
    FlowAnchor,
    FlowBlock,
    FlowBlockContext,
    FlowBlockId,
    FlowBlockMenuContext,
    FlowBounds,
    FlowDropContext,
    FlowPoint,
    FlowPort,
    FlowPortContext,
    FlowPortKind,
    FlowProperty,
    FlowSize,
    FlowTick,
    FlowWire,
    FlowWireMenuContext,
    VisualLink
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {IFocusActivator} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FocusActivator';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {
    CloudComponent,
    CloudPort,
    CloudProperty,
    CloudTick
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {FlowCanvasExtentManager} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/flow/FlowCanvasExtentManager';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ConfirmButtonSet, IConfirmDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/ConfirmDialog';
import {TextInputDialogButtonSet, TextInputDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/TextInputDialog';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action, IReactionDisposer, reaction} from 'mobx';

export class FlowCanvasController
{
    public static readonly HIDE_DEBUG_BLOCKS_AND_WIRES = true;

    private static GRID_SNAP_X_PIXELS:number = 32;
    private static GRID_SNAP_Y_PIXELS:number = 32;

    private _canvasExtentManager:FlowCanvasExtentManager = new FlowCanvasExtentManager();

    private _disposers:IReactionDisposer[] = [];

    @bind
    public init()
    {
        this._listenFlowBlocksSet();
        this._canvasExtentManager.init();
    }

    @bind
    private _listenFlowBlocksSet():void
    {
        const disposer = reaction(
            () =>
            {
                const controller = controllers.ui.flowCanvas;
                return controller.getCurrentFlowBlocks();
                // return this.getCurrentFlowBlocks();
            },
            () =>
            {
                const controller = controllers.ui.flowCanvas;
                const blocks = controller.getCurrentFlowBlocks();

                if( blocks )
                {
                    blocks.map((b:FlowBlock) =>
                    {
                        this._watchFlowBlockBounds(b);
                    });
                }
            }
        );

        this._disposers.push(disposer);
    }

    public isItOutputBlock(block:FlowBlock):boolean
    {
        const {output} = rootState.ui.visualDesigner.flow.openFlow;

        return block.id === output.id;
    }

    public isDragFromWonderbarInProgress():boolean
    {
        return !!rootState.ui.visualDesigner.flow.glass.dragContext;
    }

    public getMouseOverPort():FlowPortContext
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        return canvas.mouseOverPort;
    }

    @action.bound
    public clearMouseOverPort():void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.mouseOverPort = null;
    }

    @action.bound
    public setMouseOverPort(flowPortContext:FlowPortContext):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.mouseOverPort = flowPortContext;
    }

    // Note: this method does not modify mobx observable state
    public setMouseOverBlock(flowBlockContext:FlowBlockContext):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.mouseOverBlock = flowBlockContext;
    }

    // Note: this method does not modify mobx observable state
    public getMouseOverBlock():FlowBlockContext
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        return canvas.mouseOverBlock;
    }

    @action.bound
    private _onBlockMoved(flowBlock:FlowBlock):void
    {
        this.applyCanvasExtent();
    }

    @action
    public applyCanvasExtent():void
    {
        this._canvasExtentManager.applyCanvasExtent();
    }

    @action.bound
    public removePermanentWire(wire:FlowWire):void
    {
        if( wire && wire.isConnected() )
        {
            const outWires = wire.source.block.outWires;
            const inWires = wire.target.block.inWires;

            const sourceIdx = outWires.findIndex((w:FlowWire) => (w.id === wire.id));
            const targetIdx = inWires.findIndex((w:FlowWire) => (w.id === wire.id));

            if( sourceIdx !== -1 )
                outWires.splice(sourceIdx, 1);

            if( targetIdx !== -1 )
                inWires.splice(targetIdx, 1);
        }
    }

    @action.bound
    public addPermanentWire(flowWire:FlowWire):void
    {
        const blocks = rootState.ui.visualDesigner.flow.openFlow.flowBlocks;

        const fromBlock = blocks.find((b:FlowBlock) => (b.id === flowWire.source.block.id));
        const toBlock = blocks.find((b:FlowBlock) => (b.id === flowWire.target.block.id));

        const wireAlreadyExists1 = fromBlock.outWires.find((w:FlowWire) => (w.id === flowWire.id));
        const wireAlreadyExists2 = toBlock.inWires.find((w:FlowWire) => (w.id === flowWire.id));
        const doBothWiresExist = wireAlreadyExists1 !== undefined && wireAlreadyExists2 !== undefined;

        if( !doBothWiresExist )
        {
            const sourcePortKind = flowWire.source.port.kind;
            const targetPortKind = flowWire.target.port.kind;

            if( sourcePortKind === targetPortKind )
            {
                console.info(`cannot connect wire between ports of same kind; this is a no-op`);
                return;
            }

            this._addWire(fromBlock, flowWire, toBlock);
        }
        else if( (wireAlreadyExists1 && !wireAlreadyExists2) ||
            (!wireAlreadyExists1 && wireAlreadyExists2) )
        {
            let msg = `Error: corrupt wire state detected; inbound/outbound wires must match: `;
            msg += `addPermanentWire(wireId: ${flowWire.id}, flowWire: ${flowWire})`;
            throw new Error(msg);
        }
        else
        {
            console.info(`wire already exists -- this is a no-op`);
        }
    }

    private _addWire(fromBlock:FlowBlock, flowWire:FlowWire, toBlock:FlowBlock):void
    {
        fromBlock.outWires.push(flowWire);
        toBlock.inWires.push(flowWire);
    }

    @action.bound
    public openWireContextMenu(e:MouseEvent, wire:FlowWire):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.wireContextMenu = new FlowWireMenuContext(e, wire);
    }

    @action.bound
    public closeWireContextMenu()
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.wireContextMenu = null;
    }

    public getFlowWireMenuContext():FlowWireMenuContext | null
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        return canvas.wireContextMenu;
    }

    @action.bound
    public openBlockContextMenu(e:MouseEvent, block:FlowBlock):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.blockContextMenu = new FlowBlockMenuContext(e, block);
    }

    @action.bound
    public closeBlockContextMenu()
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.blockContextMenu = null;
    }

    public getBlockMenuContext():FlowBlockMenuContext | null
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        return canvas.blockContextMenu;
    }

    @action.bound
    public clearActiveWire():void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.activeWire = null;
    }

    @action.bound
    public setActiveWire(flowWire:FlowWire):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.activeWire = flowWire;
    }

    public getActiveWire():FlowWire | null
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        return canvas.activeWire;
    }

    public getCanvasTopLeft():FlowPoint
    {
        let canvasDomElement = this.getCanvasDomElement();

        // This occurs when switching between Flow & Surface tabs, and React un-mounts
        // the contents of a tab (thus invalidating our `canvasDomElement` reference until it
        // re-mounts the tab anew)
        if( !canvasDomElement )
            return null;

        let boundingRect = canvasDomElement.getBoundingClientRect();
        let canvasLeft = boundingRect.left;
        let canvasTop = boundingRect.top;

        return new FlowPoint(canvasLeft, canvasTop);
    }

    @action.bound
    public setBlockInstanceName(block:FlowBlock, newInstanceName:string):void
    {
        block.instanceName = newInstanceName;
    }

    @bind
    public showRenameBlockDialog(block:FlowBlock):void
    {
        const {component} = block;
        const instanceName = block.instanceName;

        const props = new TextInputDialogProps(
            MDIntent.Default,
            `Rename block`,
            null,
            `${component.name}`,
            'Enter block name',
            instanceName,
            null,
            TextInputDialogButtonSet.OK_CANCEL,
            (newInputValue:string) =>
            {
                this.setBlockInstanceName(block, newInputValue);
            },
            null);

        this.closeBlockContextMenu();
        controllers.ui.dialogs.showDialog(Dialogs.TextInput, false, props);
    }

    @bind
    public showDisconnectBlockDialog(block:FlowBlock):void
    {
        const component = block.component;
        const props:IConfirmDialogProps = {
            title: `Disconnect ${component.name} from visual data?`,
            content: `The ${component.name} visual component will remain on the surface`,
            buttonSet: ConfirmButtonSet.OK_CANCEL,
            onConfirmed: () =>
            {
                this.removeBlock(block);
            },
        };

        this.closeBlockContextMenu();
        controllers.ui.dialogs.showDialog(Dialogs.Confirm, false, props);
    }

    @bind
    public showDeleteBlockDialog():void
    {
        const selectedBlocks = controllers.ui.flowSelection.getSelectedBlocks();
        if(
            !selectedBlocks || selectedBlocks.length === 0
        )
            return;

        const hasOutputBlockInSelected:boolean = selectedBlocks.some((block:FlowBlock) => this.isItOutputBlock(block));
        const isOnlyOutputSelected:boolean = hasOutputBlockInSelected && selectedBlocks.length === 1;
        const countOfBlocksToBeDeleted = hasOutputBlockInSelected ? selectedBlocks.length - 1 : selectedBlocks.length;

        const userStr = selectedBlocks.length === 1
            ? NameUtil.getInstanceOrBlockName(selectedBlocks[0])
            : `${countOfBlocksToBeDeleted} block${countOfBlocksToBeDeleted > 1 ? 's' : ''}`;

        let props:IConfirmDialogProps;

        if( isOnlyOutputSelected )
        {
            props = {
                title: `Output can't be deleted`,
                buttonSet: ConfirmButtonSet.OK_CANCEL,
                isAlertMode: true
            };
        }
        else
        {
            props = {
                title: `Delete ${userStr}?`,
                content: hasOutputBlockInSelected ? `Output can't be deleted` : null,
                buttonSet: ConfirmButtonSet.DELETE_CANCEL,
                onConfirmed: () =>
                {
                    selectedBlocks.slice()
                        .forEach((block:FlowBlock) => !this.isItOutputBlock(block) && this.removeBlock(block));
                },
            };
        }

        this.closeBlockContextMenu();
        controllers.ui.dialogs.showDialog(Dialogs.Confirm, false, props);
    }

    @action.bound
    public removeBlock(block:FlowBlock):void
    {
        const {flowSelection} = controllers.ui;

        const flowBlocks = this.getCurrentFlowBlocks();

        if( !flowBlocks )
        {
            console.warn(`removeBlock() called but no current flow blocks exist`);
            return;
        }

        if( this.isItOutputBlock(block) )
        {
            console.warn(`removeBlock() called on an output block. That block cannot be deleted`);
            return;
        }

        if( !block.isVisualLink )
        {
            this._removeDebugBlocks(block);
        }

        if( flowSelection.isBlockSelected(block) )
        {
            flowSelection.removeBlockFromSelection(block);
        }

        block.inWires.map((w:FlowWire) =>
        {
            this.removePermanentWire(w);
        });

        block.outWires.map((w:FlowWire) =>
        {
            this.removePermanentWire(w);
        });

        const blockIdx = flowBlocks.findIndex((b:FlowBlock) => (b.id === block.id));

        if( blockIdx !== -1 )
        {
            flowBlocks.splice(blockIdx, 1);
        }

        this.setCurrentFlowBlocks(flowBlocks);
    }

    private _removeDebugBlocks(block:FlowBlock)
    {
        block.outWires.map((w:FlowWire) =>
        {
            const block = w.target.block;

            if( block && block.isDebugBlock )
                this.removeBlock(block);
        });
    }

    @action.bound
    public moveSelectedBlocksBy(moveDelta:FlowPoint, shouldSnapPoint:boolean):void
    {
        const controller = controllers.ui.flowSelection;
        controller.getSelectedBlocks()
            .map((block:FlowBlock) =>
            {
                this._moveBlock(block, moveDelta, shouldSnapPoint);
            });
    }

    @action.bound
    private _moveBlock(block:FlowBlock, moveDelta:FlowPoint, shouldSnapPoint:boolean)
    {
        const curPosition = block.bounds.position;
        let newPosDueToMove = curPosition.add(moveDelta).clampToZero();

        if( shouldSnapPoint )
            this.getSnappedPoint(newPosDueToMove.x, newPosDueToMove.y);

        block.bounds.position = newPosDueToMove;
    }

    @action.bound
    cloneAndSelectNewBlock(block:FlowBlock):void
    {
        const {flow} = controllers.ui;

        // 'Logical brother' blocks that have a Visual Component counterpart cannot be cloned.
        if( block.visualLink )
            return;

        // 'Debug Blocks' (for streaming visual data down to browser) cannot be cloned
        if( block.isDebugBlock )
            return;

        // 'Output Block' cannot be cloned
        if( this.isItOutputBlock(block) )
            return;

        const flowBlocks = this.getCurrentFlowBlocks();

        if( !flowBlocks )
        {
            console.warn(`Current flowBlocks do not exist in cloneAndSelectNewBlock(); taking no action`);
            return;
        }

        const newBlock = flow.createNewFlowBlock(block.component, block.bounds.makeCopy(), false, false, block.visualLink);

        this._watchFlowBlockBounds(newBlock);

        flowBlocks.push(newBlock);

        // if( !newBlock.isVisualLink )
        //     this._createAndWireUpDebugBlock(newBlock);

        controllers.ui.flowSelection.setSingleBlockSelection(newBlock);
    }

    private _createAndWireUpDebugBlock(newCloudBlock:FlowBlock, dropAt?:FlowPoint):void
    {
        if( !dropAt )
        {
            const canvasTopLeft = controllers.ui.flowCanvas.getCanvasTopLeft();
            dropAt = newCloudBlock.bounds.position.makeCopy().add(canvasTopLeft);
        }

        const outputPorts = newCloudBlock.outputPorts;
        if( outputPorts && outputPorts.length > 0 )
        {
            const debugBlock = this._createDebugBlock(newCloudBlock.component, dropAt);
            this._wireCloudToDebugBlock(newCloudBlock, debugBlock);
        }
    }

    public getWiresSvgElement():SVGElement | null
    {
        const {wiresSvgElement} = rootState.ui.visualDesigner.flow.canvas;
        return wiresSvgElement ? wiresSvgElement : null;
    }

    @action.bound
    public setWiresSvgElement(wiresSvgElement:SVGElement):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.wiresSvgElement = wiresSvgElement;
    }

    public getCanvasDomElement():HTMLElement | null
    {
        const {canvasDomElement} = rootState.ui.visualDesigner.flow.canvas;
        return canvasDomElement ? canvasDomElement : null;
    }

    @action.bound
    public setCanvasDomElement(ref:HTMLElement):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.canvasDomElement = ref;
    }

    @action
    public setCanvasFocusActivator(focusActivator:IFocusActivator):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.canvasFocusActivator = focusActivator;
    }

    public focusCanvas():void
    {
        const {canvasFocusActivator} = rootState.ui.visualDesigner.flow.canvas;

        canvasFocusActivator && canvasFocusActivator.focus();
    }

    @bind
    public findBoxFromBlockId(flowBlockId:FlowBlockId):Box | null
    {
        const currentFlowBlocks = this.getCurrentFlowBlocks();
        if( !currentFlowBlocks )
            return null;

        const block = currentFlowBlocks.find((block:FlowBlock) =>
        {
            return block.id === flowBlockId;
        });

        if( !block )
        {
            console.error(`Error: findBoxFromBlockId() could not find block for flowBlockId: ${flowBlockId}`);
            return null;
        }

        if( !block.visualLink )
        {
            console.error(`Error: findBoxFromBlockId() found block, but without visualLink, flowBlockId: ${flowBlockId}`);
            return null;
        }

        const boxId = block.visualLink.boxId;
        const box = controllers.ui.openDesign.findBoxFromId(boxId);

        if( !box )
        {
            console.error(`Error: findBoxFromBlockId() could not find box for flowBlockId: ${flowBlockId}`);
            return null;
        }

        return box;
    }

    public findBlock(flowBlockId:FlowBlockId):FlowBlock | null
    {
        const currentFlowBlocks = this.getCurrentFlowBlocks();
        if( !currentFlowBlocks )
            return null;

        return currentFlowBlocks.find((block:FlowBlock) =>
        {
            return block.id === flowBlockId;
        });
    }

    public findBlockFromBoxId(boxId:string):FlowBlock | null
    {
        const currentFlowBlocks = this.getCurrentFlowBlocks();
        if( !currentFlowBlocks )
            return null;

        return currentFlowBlocks.find((block:FlowBlock) =>
        {
            return block.visualLink && block.visualLink.boxId === boxId;
        });
    }

    @action.bound
    public addNewVisualLinkBlock(dropAtX:number,
                                 dropAtY:number,
                                 visualComponent:VisualComponent,
                                 visualLink:VisualLink):FlowBlock
    {
        const bakeryComponent = controllers.ui.artifacts.getBakeryComponentFor(visualComponent);
        const dropAt = new FlowPoint(dropAtX, dropAtY);

        return this._addBlock(bakeryComponent, dropAt, false, visualLink);
    }

    @action.bound
    public addNewBlock(dragContext:FlowDropContext):FlowBlock
    {
        const component:CloudComponent = dragContext.componentToAdd.component;
        const dropAt:FlowPoint = dragContext.point;

        const newCloudBlock = this._addBlock(component, dropAt, false, null);
        // this._createAndWireUpDebugBlock(newCloudBlock, dropAt);

        return newCloudBlock;
    }

    private _wireCloudToDebugBlock(newCloudBlock:FlowBlock, debugBlock:FlowBlock):void
    {
        const dataFrameInputPort = debugBlock.inputPorts[0];
        const target = new FlowAnchor(debugBlock, dataFrameInputPort);

        newCloudBlock.outputPorts.map((outPort:FlowPort) =>
        {
            const source = new FlowAnchor(newCloudBlock, outPort);
            const wire = new FlowWire(source, target);

            this._addWire(newCloudBlock, wire, debugBlock);
        });
    }

    private _createDebugBlock(component:CloudComponent, dropAt:FlowPoint)
    {
        // Add a 'Debug Block' so that visual data coming out of a cloud block is streamed
        // down to the browser, which in turn is used by the debug panels.
        //
        // When the design is published, these blocks are removed from the
        // serialized design state / should not be part of the runtime topology.
        //
        const OFFSET_FROM_CLOUD_BLOCK_Y = 50;
        const bakeryComponent = controllers.ui.artifacts.getBakeryComponentByName(`debug-for-${component.name}`);
        const dropPoint = dropAt.add(new FlowPoint(0, OFFSET_FROM_CLOUD_BLOCK_Y));

        const debugBlock = this._addBlock(bakeryComponent, dropPoint, true, VisualLink.forDebugBlock());
        return debugBlock;
    }

    private _addBlock(component:CloudComponent, dropAt:FlowPoint, isDebugBlock:boolean, visualLink:VisualLink | null):FlowBlock
    {
        const {flow} = controllers.ui;

        const canvasDomElement = this.getCanvasDomElement();
        if( !canvasDomElement )
        {
            console.warn(`FlowCanvas has not mounted yet, but onDrop() was already invoked on FlowCanvasController; taking no action`);
            return;
        }

        const flowBlocks = this.getCurrentFlowBlocks();
        if( !flowBlocks )
        {
            console.warn(`current flow blocks don't exist in addNewBlock(); taking no action`);
            return;
        }

        const positionWithinCanvas = this._computePositionWithinCanvas(canvasDomElement, dropAt);
        if( positionWithinCanvas === null )
        {
            // Note: we return here because user really meant to 'abort the drag drop' operation
            // since she/he dropped the block within a mouse X value of the cloud components panel.
            return;
        }

        const initialSize = FlowSize.Unknown;
        const bounds = new FlowBounds(positionWithinCanvas, initialSize);

        const newFlowBlock = flow.createNewFlowBlock(component, bounds, isDebugBlock, false, visualLink);

        this._watchFlowBlockBounds(newFlowBlock);

        flowBlocks.push(newFlowBlock);
        return newFlowBlock;
    }

    private _watchFlowBlockBounds(block:FlowBlock):void
    {
        reaction(() =>
            {
                let bounds = block.bounds;

                return {
                    t0: bounds.position.x,
                    t1: bounds.position.y,
                    t3: bounds.size.width,
                    t4: bounds.size.height,
                    t5: bounds.position,
                    t6: bounds.size
                };
            },
            () =>
            {
                this._onBlockMoved(block);
            });
    }

    @bind
    private _createNewBlockParts(component:CloudComponent):{properties:FlowProperty[], ticks:FlowTick[], inputPorts:FlowPort[], outputPorts:FlowPort[]}
    {
        const properties = component.properties.map((cloudProp:CloudProperty) => new FlowProperty(cloudProp));

        const inputPorts:FlowPort[] = component.inputPorts.map((p:CloudPort) =>
        {
            const newPortId = IdUtil.newGUID();
            return new FlowPort(newPortId, FlowPortKind.InputPort, p);
        });

        const outputPorts:FlowPort[] = component.outputPorts.map((p:CloudPort) =>
        {
            const newPortId = IdUtil.newGUID();
            return new FlowPort(newPortId, FlowPortKind.OutputPort, p);
        });

        const ticks = component.ticks.map((t:CloudTick) =>
        {
            const flowTick = new FlowTick(t);

            flowTick.interval = t.interval;
            flowTick.unit = t.units;

            return flowTick;
        });

        return {properties, ticks, inputPorts, outputPorts};
    }

    public getSnappedPoint(rawX:number, rawY:number):FlowPoint
    {
        const snapX = FlowCanvasController.GRID_SNAP_X_PIXELS;
        const snapY = FlowCanvasController.GRID_SNAP_Y_PIXELS;

        const x = Math.round(rawX / snapX) * snapX;
        const y = Math.round(rawY / snapY) * snapY;

        return new FlowPoint(x, y);
    }

    private _computePositionWithinCanvas(canvasDomElement:HTMLElement, dropPoint:FlowPoint):FlowPoint | null
    {
        const boundingRect = canvasDomElement.getBoundingClientRect();
        const x0 = boundingRect.left;
        const y0 = boundingRect.top;

        let x = dropPoint.x - x0;
        let y = dropPoint.y + y0;
        y -= 32; // 50% of flow block height

        // If user may want to abort the drag/drop a new block operation...
        if( x < 0 )
        {
            // If it's beyond this amount, the mouse cursor is definitely within the cloud
            // components panel
            if( x < -32 )
                return null;
                // Otherwise, mouse cursor is close enough that we still drop on flow canvas
            // (after clamping x to 0)
            else
                x = 0;
        }

        if( y < 0 )
            y = 0;

        return this.getSnappedPoint(x, y);
    }

    public setCurrentFlowBlocks(flowBlocks:FlowBlock[]):void
    {
        const openFlow = controllers.ui.flow.getOpenFlow();

        if( !openFlow )
            return;

        openFlow.flowBlocks = flowBlocks;
    }

    public getCurrentFlowBlocks():FlowBlock[] | null
    {
        const openFlow = controllers.ui.flow.getOpenFlow();

        if( !openFlow )
            return;

        return openFlow.flowBlocks;
    }
}
